import { Box, Container, styled, Theme, useMediaQuery } from '@mui/material';
import {
  ConnectCandidateMatchCardDataTestIds,
  ConnectCandidateMatchDashboardDataTestIds,
} from 'data-testids/CandidatePortalDataTestIds';
import { ConnectJobSeekingStatusDropdownDataTestIds } from 'data-testids/ConnectDataTestIds';
import UserSavedSchoolAPI from 'features/Connect/api/userSavedSchoolAPI';
import { MatchCardButtonGroup } from 'features/Connect/components/Buttons/MatchCardButtonGroup';
import { CandidateMatchCardSkeleton } from 'features/Connect/components/LoadingSkeletons/MatchCardLoadingSkeleton';
import { CandidateMatchDashboardHeaderSkeleton } from 'features/Connect/components/LoadingSkeletons/MatchDashboardHeaderLoadingSkeleton';
import { CandidateMatchCard } from 'features/Connect/components/MatchCard/CandidateMatchCard';
import { AcceptMatchModal } from 'features/Connect/components/Modals/AcceptMatchModal/AcceptMatchModal';
import { AlreadyConnectedModal } from 'features/Connect/components/Modals/AlreadyConnectedModal/AlreadyConnectedModal';
import { InAppMatchMessagingModal } from 'features/Connect/components/Modals/InAppMatchMessagingModal/InAppMatchMessagingModal';
import { RejectMatchModal } from 'features/Connect/components/Modals/RejectMatchModal/RejectMatchModal';
import { MoreInfoSlider } from 'features/Connect/components/MoreInfoSlider/MoreInfoSlider';
import { useEffect, useReducer, useState } from 'react';
import { SuccessSnackbar } from 'sharedComponents/Snackbar/SuccessSnackbar';
import { Vacancy } from 'types/connectTypes';
import { JobSeekingStatus } from 'types/types';
import auth from 'utils/auth';
import { CandidateDashboardViews } from '../../../../CandidatePortal/CandidateDashboardViews/CandidateDashboardViews';
import { ViewState } from '../../../../CandidatePortal/CandidateDashboardViews/utils/constants';
import { useConnectContext } from '../../Context/ConnectContextProvider';
import { CandidateMatchDashboardPaginationHeader } from './CandidateMatchDashboardPaginationHeader';
import {
  candidateMatchDashboardReducer,
  initialCandidateMatchDashboardState,
} from './utils/CandidateMatchDashboardReducer';
import { CandidateMatchDashboardActionsEnum } from './utils/constants';
import { CandidateMatchDashboardActionsInterface, DashboardModalTypes } from './utils/types';

export const CandidateMatchDashboard: React.FC = () => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const user = auth.getUser();
  const {
    connectContextActions,
    candidateMatches,
    isMoreInfoSliderOpen,
    hasInitiallyFetchedMatches,
    isFetchingMatches,
    jobSeekingStatus,
  } = useConnectContext();
  const isUserOpenToWorkAndMatches = jobSeekingStatus === JobSeekingStatus.OPEN_TO_WORK_AND_MATCHES;
  const [state, dispatch] = useReducer(
    candidateMatchDashboardReducer,
    initialCandidateMatchDashboardState
  );
  const [shouldShowSuccessToastMessage, setShouldShowSuccessToastMessage] = useState(false);

  const {
    isEndOfMatches = false,
    hasPendingMatches = false,
    currentMatchIndex = 0,
    totalPages = 0,
    displayTotal = 0,
    modalStates = {},
    actionedMatchIds = [],
  } = state;

  const candidateMatchDashboardActions: CandidateMatchDashboardActionsInterface = {
    setActionedMatchIds: (ids: string[]) =>
      dispatch({
        type: CandidateMatchDashboardActionsEnum.SET_ACTIONED_MATCH_IDS,
        payload: ids,
      }),
    setTotalPendingMatches: (total: number) =>
      dispatch({
        type: CandidateMatchDashboardActionsEnum.SET_TOTAL_PENDING_MATCHES,
        payload: total,
      }),
    setCurrentMatchIndex: (index: number) =>
      dispatch({
        type: CandidateMatchDashboardActionsEnum.SET_CURRENT_MATCH_INDEX,
        payload: index,
      }),
    setIsEndOfMatches: (isEnd: boolean) =>
      dispatch({ type: CandidateMatchDashboardActionsEnum.SET_IS_END_OF_MATCHES, payload: isEnd }),
    setHasPendingMatches: (has: boolean) =>
      dispatch({ type: CandidateMatchDashboardActionsEnum.SET_HAS_PENDING_MATCHES, payload: has }),
    setIsModalOpen: (modalName: DashboardModalTypes, isOpen: boolean) =>
      dispatch({
        type: CandidateMatchDashboardActionsEnum.SET_IS_MODAL_OPEN,
        modalName,
        payload: isOpen,
      }),
    setMatchCardVacancies: (vacancies: Vacancy[]) =>
      dispatch({
        type: CandidateMatchDashboardActionsEnum.SET_MATCH_CARD_VACANCIES,
        payload: vacancies,
      }),
    setDisplayTotal: (displayTotal: number) =>
      dispatch({
        type: CandidateMatchDashboardActionsEnum.SET_DISPLAY_TOTAL,
        payload: displayTotal,
      }),
  };

  useEffect(() => {
    const fetchSavedSchools = async () => {
      if (user && candidateMatches?.pending?.length > 0 && !hasInitiallyFetchedMatches) {
        const savedSchools = await UserSavedSchoolAPI.getSchoolsUserHasSaved();

        // Just update the saved schools IDs without modifying matches
        savedSchools.forEach((school) => {
          connectContextActions.toggleSavedSchoolIds(school.school_nces_id);
        });
      }
    };

    fetchSavedSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasInitiallyFetchedMatches]);

  useEffect(() => {
    if (candidateMatches) {
      const totalPending = candidateMatches?.pending?.length || 0;
      candidateMatchDashboardActions.setTotalPendingMatches(totalPending);
      candidateMatchDashboardActions.setHasPendingMatches(totalPending > 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateMatches]);

  const pendingMatches = candidateMatches?.pending || [];
  const currentMatch = pendingMatches[currentMatchIndex];

  const handlePageChange = (newIndex: number) => {
    candidateMatchDashboardActions.setCurrentMatchIndex(newIndex - 1);
  };

  const handleMatchActioned = (matchId: string) => {
    if (candidateMatches) {
      const updatedMatches = {
        ...candidateMatches,
        pending: candidateMatches.pending.filter((match) => match?.school?.nces_id !== matchId),
        previously_actioned: [
          ...(candidateMatches.previously_actioned || []),
          candidateMatches.pending.find((match) => match?.school?.nces_id === matchId),
        ].filter(Boolean),
      };
      connectContextActions.setCandidateMatches(updatedMatches);

      if (currentMatchIndex >= updatedMatches.pending.length && updatedMatches.pending.length > 0) {
        candidateMatchDashboardActions.setCurrentMatchIndex(updatedMatches.pending.length - 1);
      }
    }
  };

  const handleRejectMatch = async () => {
    if (currentMatch?.school?.nces_id) {
      const newActionedMatchIds = [...actionedMatchIds, currentMatch?.school?.nces_id];
      candidateMatchDashboardActions.setActionedMatchIds(newActionedMatchIds);
      handleMatchActioned(currentMatch?.school?.nces_id);
      candidateMatchDashboardActions.setDisplayTotal(displayTotal);
      candidateMatchDashboardActions.setIsModalOpen(DashboardModalTypes.REJECT, false);
    }
  };

  const handleAcceptMatch = (selectedOption: string) => {
    if (selectedOption !== 'email' && currentMatch?.school?.nces_id) {
      const newActionedMatchIds = [...actionedMatchIds, currentMatch?.school?.nces_id];
      candidateMatchDashboardActions.setActionedMatchIds(newActionedMatchIds);
      handleMatchActioned(currentMatch?.school?.nces_id);
    }
  };

  const handleAlreadyConnected = async () => {
    if (currentMatch?.school?.nces_id) {
      const newActionedMatchIds = [...actionedMatchIds, currentMatch?.school?.nces_id];
      candidateMatchDashboardActions.setActionedMatchIds(newActionedMatchIds);
      handleMatchActioned(currentMatch?.school?.nces_id);
      candidateMatchDashboardActions.setDisplayTotal(displayTotal);
      candidateMatchDashboardActions.setIsModalOpen(DashboardModalTypes.ALREADY_CONNECTED, false);
    }
  };

  const handleInAppMessaging = () => {
    if (currentMatch?.school?.nces_id) {
      const newActionedMatchIds = [...actionedMatchIds, currentMatch?.school?.nces_id];
      candidateMatchDashboardActions.setActionedMatchIds(newActionedMatchIds);
      handleMatchActioned(currentMatch?.school?.nces_id);
      candidateMatchDashboardActions.setIsModalOpen(DashboardModalTypes.ACCEPT, false);
      candidateMatchDashboardActions.setIsModalOpen(DashboardModalTypes.IN_APP_MESSAGING, false);
    }
  };

  // TODO: Add match action success toast logic here
  const matchActionedHandler = () => {
    window.scrollTo(0, 0);
  };

  return (
    <MatchDashboardContainer
      data-testid={ConnectCandidateMatchDashboardDataTestIds.DASHBOARD_CONTAINER}
    >
      <MatchDashboardHeaderContainer
        data-testid={ConnectCandidateMatchDashboardDataTestIds.PAGINATION_HEADER_CONTAINER}
      >
        {isUserOpenToWorkAndMatches &&
          (!hasInitiallyFetchedMatches ? (
            <CandidateMatchDashboardHeaderSkeleton />
          ) : (
            <CandidateMatchDashboardPaginationHeader
              onChange={handlePageChange}
              currentMatch={currentMatchIndex + 1}
              schoolName={currentMatch?.school?.name || ''}
              hasPendingMatches={hasPendingMatches}
              isEndOfMatches={isEndOfMatches}
              totalPages={totalPages}
              displayTotal={displayTotal}
            />
          ))}
      </MatchDashboardHeaderContainer>

      {!isUserOpenToWorkAndMatches ? (
        <CandidateDashboardViews
          viewState={ViewState.NOT_OPEN_TO_MATCHES}
          setShouldShowSuccessToastMessage={setShouldShowSuccessToastMessage}
        />
      ) : (
        <>
          {isFetchingMatches || (isFetchingMatches && !hasInitiallyFetchedMatches) ? (
            <MatchDashboardBodyContainer>
              <CandidateMatchCardSkeleton />
            </MatchDashboardBodyContainer>
          ) : (
            <>
              {currentMatch && !isEndOfMatches && (
                <MatchDashboardBodyContainer
                  data-testid={ConnectCandidateMatchDashboardDataTestIds.BODY_CONTAINER}
                >
                  <CandidateMatchCard
                    currentMatch={currentMatch}
                    candidateMatchDashboardActions={candidateMatchDashboardActions}
                    matchCardVacancies={state.matchCardVacancies}
                    actionedMatchIds={actionedMatchIds}
                  />
                </MatchDashboardBodyContainer>
              )}
            </>
          )}
          {!hasPendingMatches && !isEndOfMatches && !isFetchingMatches && (
            <CandidateDashboardViews viewState={ViewState.NO_PENDING_MATCHES} />
          )}
          {isEndOfMatches && <CandidateDashboardViews viewState={ViewState.END_OF_MATCHES} />}
          {isMobile && hasPendingMatches && !isEndOfMatches && (
            <MatchCardMobileButtonGroupContainer
              data-testid={ConnectCandidateMatchCardDataTestIds.BUTTON_GROUP_CONTAINER}
            >
              <MatchCardButtonGroup
                candidateMatchDashboardActions={candidateMatchDashboardActions}
                currentMatch={currentMatch}
                hasMatchBeenActioned={actionedMatchIds?.includes(currentMatch?.school?.nces_id)}
              />
            </MatchCardMobileButtonGroupContainer>
          )}
        </>
      )}
      <SuccessSnackbar
        message="Successfully saved"
        onClose={() => setShouldShowSuccessToastMessage(false)}
        open={shouldShowSuccessToastMessage}
        position={{ vertical: 'bottom', horizontal: 'left' }}
        dataTestId={ConnectJobSeekingStatusDropdownDataTestIds.SUCCESS_TOAST}
      />
      <RejectMatchModal
        currentMatch={currentMatch}
        isOpen={modalStates[DashboardModalTypes.REJECT]}
        onClose={() => {
          candidateMatchDashboardActions.setIsModalOpen(DashboardModalTypes.REJECT, false);
          matchActionedHandler();
        }}
        onSubmit={handleRejectMatch}
      />
      <AcceptMatchModal
        isOpen={modalStates[DashboardModalTypes.ACCEPT]}
        onClose={() => {
          candidateMatchDashboardActions.setIsModalOpen(DashboardModalTypes.ACCEPT, false);
          matchActionedHandler();
        }}
        openInAppMessagingModal={() =>
          candidateMatchDashboardActions.setIsModalOpen(DashboardModalTypes.IN_APP_MESSAGING, true)
        }
        currentMatch={currentMatch}
        onSubmit={handleAcceptMatch}
      />
      <AlreadyConnectedModal
        isOpen={modalStates[DashboardModalTypes.ALREADY_CONNECTED]}
        onClose={() => {
          candidateMatchDashboardActions.setIsModalOpen(
            DashboardModalTypes.ALREADY_CONNECTED,
            false
          );
          matchActionedHandler();
        }}
        onSubmit={handleAlreadyConnected}
        currentMatch={currentMatch}
      />
      <InAppMatchMessagingModal
        isOpen={modalStates[DashboardModalTypes.IN_APP_MESSAGING]}
        currentMatch={currentMatch}
        onClose={() => {
          candidateMatchDashboardActions.setIsModalOpen(DashboardModalTypes.ACCEPT, false);
          candidateMatchDashboardActions.setIsModalOpen(
            DashboardModalTypes.IN_APP_MESSAGING,
            false
          );
          matchActionedHandler();
        }}
        onSubmit={handleInAppMessaging}
      />
      <MoreInfoSlider
        school={currentMatch?.school}
        isCandidateDashboard={true}
        isOpen={isMoreInfoSliderOpen}
        closeHandler={() => connectContextActions.setIsMoreInfoSliderOpen(false)}
        openHandler={() => connectContextActions.setIsMoreInfoSliderOpen(true)}
      />
    </MatchDashboardContainer>
  );
};

const MatchDashboardContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  height: '100%',
  width: '100%',
  maxWidth: '1040px',
  padding: theme.spacing(3, 0, 8, 0),
}));

const MatchDashboardHeaderContainer = styled(Box)({
  width: '100%',
  padding: '0px',
});

const MatchDashboardBodyContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0, 2),
  },
}));

const MatchCardMobileButtonGroupContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  width: '100%',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.white.main,
  zIndex: 1000,
}));
